/**
 * What a chart looks like right now, for the one consumer that is not a chart.
 *
 * The Summary tab describes the chart the reader is looking at — its interval,
 * the indicators they chose to add with the latest value of each, and a picture
 * of it. All three live inside `ChartGroupView`: the values in its `dataRef`,
 * the picture behind a builder it deliberately does not publish.
 *
 * ## A registry rather than a prop, and why that is the smaller change
 *
 * `ChartGroupView` is explicit that the host must not hold its builder — the
 * `clearNonce` prop exists precisely so an action can reach the surface without
 * one. But a nonce is for *doing* something to the chart; this is a **read**,
 * pulled at an unpredictable moment by a component that is not a descendant, in
 * a different corner of the tree, sometimes in a different window.
 *
 * Threading a getter App → SectionGrid → ChartGroupView → back up would touch
 * four components to move one function. So the chart publishes a read-only
 * closure here, keyed by group id, and the panel asks for the group it was
 * opened for. Same pattern and same justification as `symbol-index.ts`: a fact
 * about the page rather than about a component.
 *
 * The builder itself never leaves `ChartGroupView`. What is registered is a
 * function that returns data.
 */

/** One chart, as a prompt can describe it. */
export interface ChartSnapshot {
  interval: string;
  /** Bars on screen, not bars loaded — what the reader can actually see. */
  barsShown: number;
  /** Plotted aliases, so a note can name the reader's own indicators. */
  indicators: string[];
  /** Last value per plotted series: `close`, `volume` and every indicator. */
  latest: Record<string, number>;
  /**
   * The close, downsampled to a readable handful of points, oldest first.
   *
   * **A last value is a position; this is a shape.** Without it the model can
   * say price is above its 50-day and nothing else — no base, no breakout, no
   * lower high — and if the image capture fails it has no trend information at
   * all. Thirty-odd numbers cost a few hundred tokens and are the difference
   * between "above the average" and "consolidating under the old high".
   *
   * Evenly spaced across the bars **on screen**, so it describes what the reader
   * is looking at rather than everything that was loaded.
   */
  closePath: number[];
  /** Where the window's own low and high sit, so the path has a scale. */
  windowLow?: number;
  windowHigh?: number;
  /** Change over the visible window, as a fraction. */
  windowChange?: number;
  /** Bare base64 PNG, no `data:` prefix. Absent when the capture failed. */
  imagePng?: string;
}

type Source = () => Promise<ChartSnapshot>;

const sources = new Map<string, Source>();

/**
 * Publish a chart's snapshot source. Returns the cleanup.
 *
 * Called from the build effect, so the entry lives exactly as long as the
 * surface does — a snapshot taken against a disposed builder is the failure this
 * shape avoids by construction.
 */
export function registerChartSnapshot(groupId: string, source: Source): () => void {
  sources.set(groupId, source);
  return () => {
    // Only if it is still ours. A remount registers the new one before the old
    // one's cleanup runs, and deleting unconditionally would unpublish the live
    // chart.
    if (sources.get(groupId) === source) sources.delete(groupId);
  };
}

/**
 * One chart's snapshot, or `undefined` when that chart is not on screen.
 *
 * `undefined` rather than an empty snapshot: a summary written with no chart
 * should say so, and a snapshot of zeroes would read as a flat one.
 */
export async function chartSnapshot(
  groupId: string | undefined,
): Promise<ChartSnapshot | undefined> {
  if (!groupId) return undefined;
  const source = sources.get(groupId);
  if (!source) return undefined;
  try {
    return await source();
  } catch {
    // A chart mid-teardown, a surface that lost its context. The caller writes
    // its summary without one.
    return undefined;
  }
}

/** Drop everything. For tests. */
export function clearChartSnapshots(): void {
  sources.clear();
}

/**
 * A chart's picture, by group id — for a community post (`docs/plans/community.md`).
 *
 * Its own registry beside the snapshot's, because the Summary stopped sending a
 * picture (see `ChartGroupView`) and a post is the one caller that wants only
 * the picture. Same lifetime rule: registered inside the build effect, so it can
 * never be called against a disposed surface.
 */
type ImageSource = () => Promise<string | null>;

const imageSources = new Map<string, ImageSource>();

export function registerChartImage(groupId: string, source: ImageSource): () => void {
  imageSources.set(groupId, source);
  return () => {
    if (imageSources.get(groupId) === source) imageSources.delete(groupId);
  };
}

/** A PNG data URL of the chart, or null when there is no surface or the capture failed. */
export async function chartImage(groupId: string | undefined): Promise<string | null> {
  if (!groupId) return null;
  const source = imageSources.get(groupId);
  if (!source) return null;
  try {
    return await source();
  } catch {
    return null;
  }
}
